interface Colors {
  [key: string]: string
}

interface RGBA {
  text: string,
  r: number,
  g: number,
  b: number,
  a?: number,
}

const lightenDarkenColor = (h: string, amt: number): string => {
  /* ref: https://css-tricks.com/snippets/javascript/lighten-darken-color/ */
  let col = h;
  let usePound = false;

  if (col[0] === '#') {
    col = col.slice(1);
    usePound = true;
  }

  const num = parseInt(col, 16);

  let r = (num >> 16) + amt;

  if (r > 255) r = 255;
  else if (r < 0) r = 0;

  let b = ((num >> 8) & 0x00FF) + amt;

  if (b > 255) b = 255;
  else if (b < 0) b = 0;

  let g = (num & 0x0000FF) + amt;

  if (g > 255) g = 255;
  else if (g < 0) g = 0;

  return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16);
};

const hexToRGBA = (hexCode: string, alpha?: number): RGBA => {
  /* ref: https://gist.github.com/danieliser/b4b24c9f772066bcf0a6 */
  let hex: string = hexCode.replace('#', '');
  let a: number = alpha ?? 1;

  if (hex.length === 3) hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  /* Backward compatibility for whole number based opacity values. */
  if (a > 1 && a <= 100) a /= 100;

  return {
    text: `rgba(${+r}, ${+g}, ${+b}, ${a})`,
    r: +r,
    g: +g,
    b: +b,
    a: +a,
  };
};

const RGBAToRGB = (r: number, g: number, b: number, a: number, r2: number, g2: number, b2: number): RGBA => {
  /* ref: https://gist.github.com/tqc/2564280 */
  const r3 = Math.round(((1 - a) * r2) + (a * r));
  const g3 = Math.round(((1 - a) * g2) + (a * g));
  const b3 = Math.round(((1 - a) * b2) + (a * b));
  return {
    text: `rgb(${+r3}, ${+g3}, ${+b3})`,
    r: +r3,
    g: +g3,
    b: +b3,
  };
};

const RGBToHex = (r: number, g: number, b: number): string => {
  /* ref: https://css-tricks.com/converting-color-spaces-in-javascript/ */
  let r2 = r.toString(16);
  let g2 = g.toString(16);
  let b2 = b.toString(16);

  if (r2.length === 1) r2 = `0${r2}`;
  if (g2.length === 1) g2 = `0${g2}`;
  if (b2.length === 1) b2 = `0${b2}`;

  return `#${r2}${g2}${b2}`;
};

const hexAddAlpha = (hexCode: string, alpha: number) => {
  const rgba = hexToRGBA(hexCode, alpha);
  const rgb = RGBAToRGB(rgba.r, rgba.g, rgba.b, rgba.a ?? 1, 255, 255, 255);
  const resultHex = RGBToHex(rgb.r, rgb.g, rgb.b);
  return resultHex;
};

export {
  Colors,
  lightenDarkenColor,
  hexToRGBA,
  RGBAToRGB,
  RGBToHex,
  hexAddAlpha,
};
